<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { auth0 } from '@/services/auth0';
import { useAuth0 } from '@auth0/auth0-vue';
import { useContextStore, useTenantStore } from '@/store';

import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useRoute } from 'vue-router';
import {
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  Bars3Icon,
  MagnifyingGlassCircleIcon,
  UserGroupIcon,
  ChartBarIcon,
  XMarkIcon,
  BuildingOffice2Icon,
SquaresPlusIcon,
InboxIcon,
} from '@heroicons/vue/24/solid'
import type { AdminRole } from "@upclaim/sdk/src/extra-types";
import { ScaleIcon } from '@heroicons/vue/24/solid';
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';
import { FilePenLine, Signature } from 'lucide-vue-next';
import router from '@/router';

interface Navigation {
  name: string;
  href: string;
  permissions: Array<AdminRole> | "*";
  icon: any;
  current: boolean;
  countId?: string;
}

const navigation: Navigation[][] = [
  [
    { name: 'Dashboard', href: '/claims', permissions: "*", countId: "count-claims", icon: HomeIcon, current: true },
    { name: 'Contentieux', href: '/procedures', permissions: ["UPCLAIM_ADMIN", "LAWYER", "PLEADING_LAWYER"], icon: ScaleIcon, current: false },
    { name: 'Nouveau dossier', permissions: "*", href: '/claims/create', icon: SquaresPlusIcon, current: false },
    { name: 'Signatures', permissions: ["UPCLAIM_ADMIN"], href: '/signature-requests', icon: FilePenLine, current: false },
    // { name: 'Audiences', href: '/calendar', permissions: ["UPCLAIM_ADMIN", "LAWYER", "PLEADING_LAWYER"], icon: CalendarDaysIcon, current: false },
    // { name: 'Recherche avancée', href: '#', icon: MagnifyingGlassCircleIcon, current: false },
  ],
  [
    { name: 'Utilisateurs', href: '/users', permissions: ["UPCLAIM_ADMIN", "CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"], icon: UserGroupIcon, current: false },
    { name: 'Entreprises', href: '/companies', permissions: ["UPCLAIM_ADMIN", "CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"], icon: BuildingOffice2Icon, current: false },
    // { name: 'Agences de voyages', href: '/travel-agency', permissions: ["UPCLAIM_ADMIN"], icon: UserGroupIcon, current: false },
    { name: 'Reporting', href: '/reporting', permissions: ["UPCLAIM_ADMIN"], icon: ChartBarIcon, current: false }
  ]
].filter(nav => nav.some(item => computePermissionForCurrentUser(item.permissions))) as Navigation[][]

const sidebarOpen = ref(false);
const user = ref<any>(useAuth0().user.value);

async function logoutMethod() {
  await auth0.logout();
}

// onMounted(async () => {
//   const tenant = await useTenantStore().retrieveCurrentTenant();
//   console.log("1111", tenant)
//   if (tenant.tenantOnboarding?.completedAt) {
//     console.log(tenant)
//     // alert(tenant)
//   } else {
//     // Un-comment when ready
//     console.log("22222222")
//     // router.push({ name: "DomainOnboarding" });
//   }
// });
</script>

<template>
  <div v-if="!useContextStore().isPlatformBrowser">
    <router-view></router-view>
  </div>
  <div v-else class="flex h-screen">
    <!-- Static sidebar for desktop -->
    <div class="hidden lg:flex lg:flex-shrink-0">
      <div class="flex flex-col lg:w-56 2xl:w-64">
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex flex-col flex-1 min-h-0 border-r border-gray-200 bg-[linear-gradient(115deg,var(--tw-gradient-stops))] from-stone-50 from-[28%] via-stone-100 via-[70%] to-white sm:bg-[linear-gradient(145deg,var(--tw-gradient-stops))">
          <div class="flex flex-col flex-1 pt-5 pb-4 overflow-y-auto">
            <div class="flex items-center flex-shrink-0 px-4">
              <img class="w-auto h-8" src="/logo_blue.svg" alt="Upclaim" />
            </div>
            <nav class="flex-1 mt-5" aria-label="Sidebar">
              <div class="px-2 divide-y-2 divide-gray-200">
                  <div v-for="(navGroup, i) of navigation" :key="i" class="">
                    <div class="pt-1 divide-transparent divide-y-1" v-if="navGroup.some(item => computePermissionForCurrentUser(item.permissions))">
                      <template v-for="item in navGroup" :key="item.name">
                        <div v-can="item.permissions">
                          <router-link
                            :to="item.href"
                            v-slot="{ isActive }"
                          >
                            <div
                              :class="[
                                isActive ? ' bg-gradient-to-r from-zinc-200 to-slate-200 text-gray-900' : 'text-gray-600 hover:bg-stone-100 hover:text-gray-900',
                                'group flex items-center px-2 py-2 text-sm mb-1 font-medium rounded-md'
                              ]"
                            >
                              <component
                                :is="item.icon"
                                :class="[
                                  isActive ? 'text-stone-900' : 'text-gray-400 group-hover:text-gray-500',
                                  'mr-3 h-6 w-6'
                                ]"
                                aria-hidden="true"
                              />
                              {{ item.name }}
                              <span v-if="item.countId && isActive" :id="item.countId" class="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200" aria-hidden="true">
                                <!-- {{ item.count }} -->
                              </span>
                            </div>
                          </router-link>
                        </div>
                      </template>
                    </div>
                    <!-- <div v-if="i < navigation.length - 1 && false" class="w-full my-2 border-t-2 border-gray-200">
                      
                    </div> -->
                  </div>
              </div>
            </nav>
          </div>
          <div class="flex flex-shrink-0 p-4 border-t border-gray-200">
            <a href="#" class="flex-shrink-0 block w-full group">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <img class="rounded-full w-9 h-9" :src="user.picture" referrerpolicy="no-referrer" alt="" />
                </div>
                <div class="flex-grow ml-3 text-left">
                  <p class="w-40 text-xs font-medium text-gray-700 truncate group-hover:text-gray-900">{{ user.name }}</p>
                  <a @click="logoutMethod" class="text-xs font-medium text-gray-500 group-hover:text-gray-700">Me deconnecter</a>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile sidebar -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-40 lg:hidden" @close="sidebarOpen = false">
        <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </TransitionChild>

        <div class="fixed inset-0 z-40 flex">
          <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
            <DialogPanel class="relative flex flex-col flex-1 w-full max-w-xs bg-white focus:outline-none">
              <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
                <div class="absolute top-0 right-0 pt-2 -mr-12">
                  <button type="button" class="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" @click="sidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="w-6 h-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
              <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div class="flex items-center flex-shrink-0 px-4">
                  <img class="w-auto h-8" src="/logo_blue.svg" alt="Upclaim" />
                </div>
                <nav aria-label="Sidebar" class="mt-5">
                  <div class="px-2 space-y-1">
                    <a v-for="item in navigation.flat()" :key="item.name" :href="item.href" :class="[useRoute().path.startsWith(item.href) ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'group flex items-center px-2 py-2 text-base font-medium rounded-md']">
                      <component :is="item.icon" :class="[item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500', 'mr-4 h-6 w-6']" aria-hidden="true" />
                      {{ item.name }}
                    </a>
                  </div>
                </nav>
              </div>
              <div class="flex flex-shrink-0 p-4 border-t border-gray-200">
                <a href="#" class="flex-shrink-0 block group">
                  <div class="flex items-center">
                    <div>
                      <img class="inline-block w-10 h-10 rounded-full" src="https://media-exp1.licdn.com/dms/image/C5603AQFDjklqq51AMQ/profile-displayphoto-shrink_400_400/0/1601478331423?e=1660780800&v=beta&t=71VRji-Nfvwy3LtTjdc71sbxTP7llr398WuoyoatwHQ" alt="" />
                    </div>
                    <div class="ml-3">
                      <p class="text-base font-medium text-gray-700 group-hover:text-gray-900">{{ user.name }}</p>
                      <p class="text-sm font-medium text-gray-500 group-hover:text-gray-700">{{ user.email }}</p>
                    </div>
                  </div>
                </a>
              </div>
            </DialogPanel>
          </TransitionChild>
          <div class="flex-shrink-0 w-14" aria-hidden="true">
            <!-- Force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Top menu to toggle mobile sidebar -->
    <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
      <div class="lg:hidden">
        <div class="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-1.5">
          <div>
            <img class="w-auto h-8" src="/logo_blue.svg" alt="Upclaim" />
          </div>
          <div>
            <button type="button" class="inline-flex items-center justify-center w-12 h-12 -mr-3 text-gray-500 rounded-md hover:text-gray-900" @click="sidebarOpen = true">
              <span class="sr-only">Open sidebar</span>
              <Bars3Icon class="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <!-- Main section -->
      <main class="relative z-0 flex flex-1 overflow-hidden">
        <router-view></router-view>
      </main>
    </div>
  </div>

</template>